import { getConfig, getErrorMessage, klinikenApi } from "@/api";
import dialogEvents from "@/dialogEvents";
import { formatMessage, nonFieldErrors } from "@/store/utils";
import { flashMessage, openDialog } from "@/utils";
import emitter from "tiny-emitter/instance";

const STATUS_EMPTY = "empty";
const STATUS_MODIFIED = "modified";
const STATUS_SAVING = "saving";
const STATUS_SAVED = "saved";
const STATUS_SENDING = "sending";
const SEND_STATUSES = [STATUS_MODIFIED, STATUS_SAVED];
const WORKING_STATUSES = [STATUS_SENDING, STATUS_SAVING];

const getDefaultState = () => {
    return {
        status: STATUS_EMPTY,
        operations: [],
        alreadybooked: {},
        params: {},
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        reset(state) {
            Object.assign(state, getDefaultState());
        },
        setStatus(state, status) {
            state.status = status;
        },
        setOpAnmalans(state, payload) {
            state.operations = payload;
        },
        setOpAnmala(state, item) {
            const modifiedOperations = [...state.operations];
            const index = modifiedOperations.findIndex((i) => i.id === item.id);

            if (index !== -1) {
                modifiedOperations[index] = item;
                state.operations = modifiedOperations;
            }
        },
        setPreanestesibedomningar(state, payload) {
            state.preanestesibedomningar = payload;
        },
        setAlreadyBooked(state, payload) {
            state.alreadybooked = payload;
        },
        setParams: (state, params) => (state.params = params),
    },
    getters: {
        opAnmalans(state) {
            return state.operations;
        },
        preanestesibedomningar(state) {
            return state.preanestesibedomningar;
        },
        isSendable(state) {
            return SEND_STATUSES.includes(state.status);
        },
        isWorking(state) {
            return WORKING_STATUSES.includes(state.status);
        },
        isModified(state) {
            return state.status === STATUS_MODIFIED;
        },
        getAlreadyBooked: (state) => state.alreadybooked,
        getParams: (state) => state.params,
        getOpanmalanById: (state) => (id) => state.operations.find((operation) => operation.id === id),
        getOpanmalningarningarByIds: (state) => (ids) => {
            return state.operations.filter((operation) => ids.includes(operation.id));
        },
    },
    actions: {
        updateParams({ commit }, params) {
            commit("setParams", params);
        },
        async openOpAnmalan({ dispatch, rootState }, { component, id, patient, mode }) {
            await dispatch("tabs/closeTab", component, { root: true });
            if (patient !== rootState.patientData.currentId)
                await dispatch("patientData/loadFromBackend", { id: patient }, { root: true });
            await dispatch("tabs/openTab", { component, parameters: { id, mode } }, { root: true });
        },

        async save({ rootState, dispatch, commit, state }, { data, tabId }) {
            data.patient = rootState.patientData.currentId;

            const config = getConfig({}, "Operationsanmälan");

            if (!data.id) {
                return await klinikenApi
                    .post("/operation/anmalan/", data, config)
                    .then((response) => {
                        dispatch(
                            "operation/operationsanmalanV2/getOperationsanmalanById",
                            {
                                id: data.id,
                                addAsNew: true,
                                forceRefresh: true,
                            },
                            { root: true }
                        );

                        flashMessage("Din anmälan har skickats.", 5000);

                        return response.data;
                    })
                    .catch((error) => {
                        if (error?.response?.status !== 400) {
                            openDialog("Misslyckades med att skicka anmälan. ", "error");
                        }
                        throw error;
                    });
            } else {
                return await klinikenApi
                    .put(`/operation/anmalan/${data.id}/`, data, config)
                    .then((response) => {
                        const item = state.operations.find((item) => item.id === data.id);
                        const newitem = Object.assign({}, item, response.data);

                        commit("setOpAnmala", newitem);
                        flashMessage("Operationsanmälan uppdaterad", 5000);
                        emitter.emit("operationsanmalan-uppdaterad", newitem);

                        return response.data;
                    })
                    .catch((error) => {
                        // Om överbokning
                        if (error.response.status === 400) {
                            const { non_field_errors, retroaktiv_bokning } = error.response.data;
                            const { list: overbokningar } = nonFieldErrors(non_field_errors, "overbokningar");
                            const retroaktivBokningList = retroaktiv_bokning ? retroaktiv_bokning : [];
                            const errorOverbokningar = formatMessage(overbokningar, "overbokningar");
                            const errorRetroaktivBokning = formatMessage(retroaktivBokningList, "retroaktiv_bokning");
                            const errorList = [...errorOverbokningar, ...errorRetroaktivBokning];

                            if (errorList.length) {
                                dialogEvents.$emit("open", {
                                    type: "warning",
                                    errorList: errorList,
                                    description: "",
                                    buttons: [
                                        {
                                            title: "Avbryt",
                                            type: "outline-primary",
                                        },
                                        {
                                            title: "Gå vidare med bokning ändå",
                                            type: "primary",
                                            action: async () => {
                                                if (overbokningar.length) {
                                                    data.overboka = true;
                                                } else {
                                                    data.retroaktiv_bokning = true;
                                                }
                                                await dispatch("save", { data, tabId });
                                            },
                                        },
                                    ],
                                });
                            } else {
                                throw error;
                            }
                        } else {
                            openDialog("Misslyckades med att redigera anmälan. ", "error");
                            throw error;
                        }
                    });
            }
        },

        async bokaOpAnmalan({ commit, state }, { payload }) {
            try {
                await klinikenApi.patch(`operation/anmalan/${payload.id}/boka/`, payload, getConfig());

                // Hämtning görs efter patch, pga att /boka endpoint bara returnerar delar av anmälan
                const { data } = await klinikenApi.get(`operation/anmalan/${payload.id}/`, getConfig());

                const item = state.operations.find((item) => item.id === payload.id);
                const newitem = Object.assign({}, item, data);

                commit("setOpAnmala", newitem);
                flashMessage(newitem.preliminarbokning ? "Operationen preliminärbokad" : "Operationen bokad", 5000);
                emitter.emit("operationsanmalan-bokad", newitem);

                return newitem;
            } catch (error) {
                const { non_field_errors, retroaktiv_bokning } = error.response ? error.response.data : {};
                const { list: overbokningar } = nonFieldErrors(non_field_errors, "overbokningar");
                const retroaktivBokningList = retroaktiv_bokning ? retroaktiv_bokning : [];
                const errorOverbokningar = formatMessage(overbokningar, "overbokningar");
                const errorRetroaktivBokning = formatMessage(retroaktivBokningList, "retroaktiv_bokning");
                const errorList = [...errorOverbokningar, ...errorRetroaktivBokning];

                if (errorList.length) {
                    await commit("setAlreadyBooked", {
                        overbokad: errorOverbokningar.length > 0,
                        retroaktiv_bokning: errorRetroaktivBokning.length > 0,
                        errorList: errorList,
                    });
                } else {
                    openDialog(`Kunde inte boka operationen. ${getErrorMessage(error)}`, "error");
                }

                return null;
            }
        },
        resetTvingadBokning({ commit }) {
            commit("setAlreadyBooked", {
                overbokad: false,
                retroaktiv_bokning: false,
                errorList: [],
            });
        },
        resetOpAnmalan({ commit }) {
            commit("setOpAnmalans", []);
        },
        async avbokaOperationAnmalan({ state }, payload) {
            try {
                await klinikenApi.patch(`operation/anmalan/${payload.id}/avboka/`, payload, getConfig());

                // Hämtning görs efter patch, pga att /avboka endpoint bara returnerar delar av anmälan
                const { data } = await klinikenApi.get(`operation/anmalan/${payload.id}/`, getConfig());

                flashMessage(payload.avfors_vantelista === "NEJ" ? "Operation avbokad" : "Operation struken");

                const item = state.operations.find((item) => item.id === payload.id);
                const newitem = Object.assign({}, item, data);

                emitter.emit("operationsanmalan-avbokad", newitem);
            } catch (error) {
                console.error(error);
                openDialog("Kunde inte avboka operationen. ", "error");
            }
        },
        async avvaktaOperationAnmalan({ state }, payload) {
            /* eslint-disable no-useless-catch */
            try {
                const response = await klinikenApi.patch(
                    `operation/anmalan/${payload.id}/avvakta/`,
                    payload,
                    getConfig()
                );

                // Hämtning görs efter patch, pga att /avvakta endpoint bara returnerar delar av anmälan
                const { data } = await klinikenApi.get(`operation/anmalan/${payload.id}/`, getConfig());

                flashMessage("Operationen avvaktad");

                const item = state.operations.find((item) => item.id === payload.id);
                const newitem = Object.assign({}, item, data);

                emitter.emit("operationsanmalan-uppdaterad", newitem);

                return response;
            } catch (error) {
                throw error;
            }
        },
        async taBortPreliminarbokningById({ commit, state }, id) {
            emitter.emit("open-dialog", {
                type: "information",
                description: "Vill du ta bort preliminärbokningen?",
                lines: ["Operationen kommer inte längre att visas som preliminärbokad i operationskalendern."],
                buttons: [
                    {
                        title: "Avbryt",
                        type: "secondary",
                    },
                    {
                        title: "Ja, ta bort preliminärbokning",
                        type: "primary",
                        action: async () => {
                            try {
                                await klinikenApi.patch(
                                    `operation/anmalan/${id}/avboka/`,
                                    { preliminarbokning: false },
                                    getConfig()
                                );

                                // Hämtning görs efter patch, pga att /avboka endpoint bara returnerar delar av anmälan
                                flashMessage("Preliminärbokning borttagen");

                                const { data } = await klinikenApi.get(`operation/anmalan/${id}/`, getConfig());

                                const item = state.operations.find((item) => item.id === id);
                                const newitem = Object.assign({}, item, data);

                                emitter.emit("operationsanmalan-avbokad", newitem);
                            } catch (error) {
                                openDialog(
                                    "Kunde inte ta bort preliminärbokningen. " + getErrorMessage(error),
                                    "error"
                                );
                            }
                        },
                    },
                ],
            });
        },
    },
};
